'use client'

import { FC, ReactNode } from 'react'
import { UseFormReturn } from 'react-hook-form'
import Image from 'next/image'
import { Button } from '@repo/ui/button'
import { Input } from '@repo/ui/input'
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@repo/ui/form'
import { useFormError } from '@/hooks/useFormError'
import { TermsFooter } from '../TermsFooter'
import { AuthResult } from '@/types'

type AuthMode = 'signin' | 'signup'

type BaseFormData = {
  email: string
  password?: string
}

type Props<T extends BaseFormData> = {
  form: UseFormReturn<T>
  onEmailSubmit: (data: T) => Promise<AuthResult | null>
  onGoogleAuth: () => Promise<{ success: boolean; error?: string }>
  authMode: AuthMode
  onSwitchMode: () => void
  children?: ReactNode
}

const BaseAuthForm = <T extends BaseFormData>({
  form,
  onEmailSubmit,
  onGoogleAuth,
  authMode,
  onSwitchMode,
  children,
}: Props<T>) => {
  const { error, isLoading, handleAsyncOperation } = useFormError()

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  const handleGoogleAuth = async () => {
    await handleAsyncOperation(onGoogleAuth, 'Google sign-in failed. Please try again or use email sign-in.')
  }

  const onSubmit = async (data: T) => {
    await handleAsyncOperation(() => onEmailSubmit(data), 'Failed to send verification email. Please try again.')
  }

  const isFormLoading = isLoading || isSubmitting

  return (
    <div className="mt-6 sm:mt-10 w-full">
      <p className="text-xl font-medium text-grey-800 text-center">{authMode === 'signin' ? 'Sign In' : 'Sign Up'}</p>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <form className="mt-4 sm:mt-8" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name={'email' as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs sm:text-sm font-medium text-grey-700">Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    className="text-base font-regular border-grey-200 text-grey-800 placeholder:text-grey-400 bg-middleground"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-sm text-red-600" />
              </FormItem>
            )}
          />

          {children}
        </div>

        <div className="flex w-full mt-6">
          <Button type="submit" className="w-full min-h-10 text-base font-medium" disabled={isFormLoading}>
            {isFormLoading ? 'Sending...' : authMode === 'signin' ? 'Sign In' : 'Sign Up'}
          </Button>
        </div>
      </form>

      <Button
        onClick={handleGoogleAuth}
        variant="outline"
        className="mt-4 mb-8 bg-middleground px-4 min-h-10 w-full py-2 gap-3"
      >
        <Image alt="Google" src="/google.svg" width={24} height={24} />
        <span className="text-grey-800 text-base font-medium">Continue with Google</span>
      </Button>

      <TermsFooter />

      <div className="mt-5 sm:mt-8 flex gap-1 w-full items-center justify-center">
        <span className="text-sm sm:text-base font-regular text-grey-600">
          {authMode === 'signin' ? "Don't have an account?" : 'Already have an account?'}
        </span>
        <Button variant="link" className="text-sm sm:text-base font-medium text-grey-800 p-0" onClick={onSwitchMode}>
          {authMode === 'signin' ? 'Sign up' : 'Sign in'}
        </Button>
      </div>

      <div className="mt-3 flex gap-1 w-full items-center justify-center">
        <span className="text-sm sm:text-base font-regular text-mint-800">New Biobank?</span>
        <a href="https://airtable.com/apphPbiLk5RuXYGwD/pagA3GP2vFUy3BCLt/form" target="_blank">
          <Button variant="link" className="text-sm sm:text-base font-medium text-grey-800 p-0">
            Contact us
          </Button>
        </a>
      </div>
    </div>
  )
}

export default BaseAuthForm
